import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Bookmark, FileDown, Info } from "lucide-react";
import { API, api, errMsg, specValue } from "@/lib/api";
import { useSpecSchema } from "@/context/SpecSchemaContext";
import { useAuth } from "@/context/AuthContext";
import { MandatoryTag, StatusChip, StatusLegend } from "@/components/StatusChip";
import { Btn, Card, Empty, Loading } from "@/components/Ui";

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const schema = useSpecSchema();

  const [error, setError] = useState("");

  useEffect(() => {
    setData(null);
    api.get(`/products/${id}/compare`).then((r) => setData(r.data))
      .catch(() => setError("This product could not be loaded."));
  }, [id]);

  const save = async () => {
    try {
      await api.post("/saved", { type: "product", ref_id: id,
        label: `${data.base.manufacturer_name} ${data.base.part_number}` });
      toast.success("Saved to your account");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (error)
    return (
      <div className="mx-auto max-w-[1240px] px-5 py-20 text-center" data-testid="product-error">
        <p className="text-sm text-[#B42318]">{error}</p>
        <Link to="/search" className="mt-4 inline-block text-sm font-bold text-[#102A43] underline">
          Back to search
        </Link>
      </div>
    );
  if (!data) return <div className="mx-auto max-w-[1240px] px-5"><Loading label="Loading technical data…" /></div>;

  const { base, parameters, comparisons } = data;
  // Baseline fields follow the product's own category, not a fixed cable-gland list.
  const displayFields = schema.displayFor(data.category || base.category);


  return (
    <div className="mx-auto max-w-[1240px] px-5 py-10" data-testid="product-detail-page">
      <Link to="/search" data-testid="back-to-search-link"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#667788] hover:text-[#102A43]">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to search
      </Link>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#147D78]">
            {base.manufacturer_name} · {base.category}
            {base.subcategory ? ` · ${base.subcategory}` : ""}
          </p>
          <h1 data-testid="product-part-number"
            className="mt-2 font-display text-3xl font-black tracking-tight text-[#102A43] sm:text-4xl">
            {base.part_number}
          </h1>
          <p className="mt-1 text-sm text-[#667788] md:text-base">{base.name}</p>
          {base.country_of_manufacture && (
            <p className="mt-1 text-xs text-[#8598ac]">Country of manufacture: {base.country_of_manufacture}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {base.datasheet_file_id && (
            <a href={`${API}/files/${base.datasheet_file_id}`} target="_blank" rel="noreferrer">
              <Btn variant="ghost" data-testid="datasheet-btn">
                <FileDown className="h-4 w-4" /> Datasheet
              </Btn>
            </a>
          )}
          {user && user.role !== "admin" && (
            <Btn variant="ghost" data-testid="save-crossref-btn" onClick={save}>
              <Bookmark className="h-4 w-4" /> Save
            </Btn>
          )}
          <Link to={`/rfq?product=${base.id}`}>
            <Btn variant="gold" data-testid="request-quote-btn">Request Quote</Btn>
          </Link>
        </div>
      </div>

      <Card className="mt-8" data-testid="normalized-spec">
        <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
          Normalized technical specification
        </h2>
        <p className="mt-1 text-sm text-[#667788]">
          This specification is the requirement baseline for the comparison below. Mandatory
          parameters are marked.
        </p>
        <dl className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          {displayFields.map(([key, label, field]) => {
            const meta = parameters.find((p) => p.key === key);
            return (
              <div key={key} data-testid={`spec-${key}`} className="border-b border-[#DFE6ED] pb-3">
                <dt className="flex items-center text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">
                  {label}
                  <MandatoryTag mandatory={!!meta?.mandatory} />
                </dt>
                <dd className="mt-1 text-sm font-semibold text-[#18232F]">{schema.specValue(base.spec, field)}</dd>
              </div>
            );
          })}
        </dl>
        {base.technical_notes && (
          <div className="mt-5 rounded-[10px] border-l-4 border-[#147D78] bg-[#EAF4F3] p-4"
            data-testid="product-supplier-notes">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#147D78]">
              <Info className="h-3.5 w-3.5" /> Additional information from the supplier
            </p>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[#334e68]">
              {base.technical_notes}
            </p>
          </div>
        )}
      </Card>

      <section className="mt-10" data-testid="comparison-section">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-black tracking-tight text-[#102A43]">
              Technically comparable products
            </h2>
            <p className="mt-1 text-sm text-[#667788]">
              Each parameter is assessed individually against the baseline. No percentage scores are used.
            </p>
          </div>
          <StatusLegend />
        </div>

        {comparisons.length === 0 ? (
          <Empty title="No mapped equivalents yet"
            sub="Submit an RFQ and our engineering team will identify an equivalent."
            action={<Link to={`/rfq?product=${base.id}`}><Btn variant="gold">Submit an RFQ</Btn></Link>} />
        ) : (
          <>
            <div className="mt-6 hidden overflow-x-auto rounded-[14px] border border-[#DFE6ED] bg-white lg:block">
              <table className="spec-table w-full border-collapse text-sm" data-testid="comparison-matrix">
                <thead>
                  <tr className="bg-[#102A43] text-white">
                    <th className="text-[10px] font-semibold uppercase tracking-[0.12em]">Parameter</th>
                    <th className="text-[10px] font-semibold uppercase tracking-[0.12em]">
                      Requirement<br />
                      <span className="font-normal normal-case tracking-normal text-[#bcccdc]">
                        {base.manufacturer_name} {base.part_number}
                      </span>
                    </th>
                    {comparisons.map((c) => (
                      <th key={c.product.id} className="min-w-[210px] text-[10px] font-semibold uppercase tracking-[0.12em]">
                        {c.product.manufacturer_name}<br />
                        <span className="font-normal normal-case tracking-normal text-[#bcccdc]">
                          {c.product.part_number}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parameters.map((p) => (
                    <tr key={p.key} className="transition-colors hover:bg-[#f8fafc]">
                      <td className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">
                        {p.label}
                        <MandatoryTag mandatory={p.mandatory} />
                      </td>
                      <td className="font-semibold text-[#18232F]">{specValue(base.spec, p.key)}</td>
                      {comparisons.map((c) => {
                        const row = c.parameters.find((x) => x.key === p.key);
                        return (
                          <td key={c.product.id} data-testid={`cell-${c.product.id}-${p.key}`}>
                            <div className="font-medium text-[#18232F]">{row?.offered}</div>
                            <div className="mt-1.5"><StatusChip status={row?.status} compact /></div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="bg-[#F5F7FA]">
                    <td className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">
                      Supplier notes
                    </td>
                    <td className="align-top text-xs leading-relaxed text-[#334e68]">
                      {base.technical_notes || <span className="text-[#8598ac]">—</span>}
                    </td>
                    {comparisons.map((c) => (
                      <td key={c.product.id} className="align-top text-xs leading-relaxed text-[#334e68]"
                        data-testid={`notes-${c.product.id}`}>
                        {c.product.technical_notes || <span className="text-[#8598ac]">—</span>}
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-[#F5F7FA]">
                    <td className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">Overall</td>
                    <td className="text-xs font-semibold text-[#667788]">Baseline</td>
                    {comparisons.map((c) => (
                      <td key={c.product.id}>
                        <StatusChip status={c.verdict} testId={`verdict-${c.product.id}`} />
                        <div className="mt-2 flex gap-2">
                          <Link to={`/products/${c.product.id}`} className="text-xs font-bold text-[#102A43] underline">
                            Details
                          </Link>
                          <Link to={`/rfq?product=${c.product.id}`} data-testid={`compare-quote-btn-${c.product.id}`}
                            className="text-xs font-bold text-[#147D78] underline">
                            Request quote
                          </Link>
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 space-y-4 lg:hidden">
              {comparisons.map((c) => (
                <Card key={c.product.id} data-testid={`comparison-card-${c.product.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#147D78]">
                        {c.product.manufacturer_name}
                      </p>
                      <h3 className="font-display text-base font-black text-[#102A43]">{c.product.part_number}</h3>
                    </div>
                    <StatusChip status={c.verdict} compact />
                  </div>
                  <div className="mt-4 space-y-3 border-t border-[#DFE6ED] pt-4">
                    {c.parameters.map((row) => (
                      <div key={row.key}>
                        <p className="flex items-center text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">
                          {row.label}
                          <MandatoryTag mandatory={row.mandatory} />
                        </p>
                        <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                          <span className="text-sm font-medium text-[#18232F]">{row.offered}</span>
                          <StatusChip status={row.status} compact />
                        </div>
                        <p className="mt-0.5 text-xs text-[#8598ac]">Required: {row.required}</p>
                      </div>
                    ))}
                  </div>
                  <Link to={`/rfq?product=${c.product.id}`} className="mt-5 block">
                    <Btn className="w-full">Request quote for this part</Btn>
                  </Link>
                </Card>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
